import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Grid, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import RMSIconButton from '../../../shared/components/buttons/RMSIconButton';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import { formatDateTime } from '../../../shared/utility/dateFormatter';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import { removePersonTag } from '../../services/userService';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import { resolveDescription, resolveIcon } from '../../utility/personTagsSummaryCardHelper';

const PersonTagsSummaryCard = ({ tags = [], onAddTag, onTagDeleted }) => {
  const dispatch = useDispatch();
  const { tagTypeOptions } = useUserDictionaryOptions();
  const [selectedTag, setSelectedTag] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const activeTags = useMemo(() => {
    const latestByType = new Map();

    tags
      .filter((tag) => !tag.IsDeleted)
      .forEach((tag) => {
        const existing = latestByType.get(tag.Type);
        if (!existing || new Date(tag.Created) > new Date(existing.Created)) {
          latestByType.set(tag.Type, tag);
        }
      });

    return Array.from(latestByType.values());
  }, [tags]);

  const handleCloseDetails = useCallback(() => setSelectedTag(null), []);

  const handleDelete = useCallback(() => {
    requestConfirm(
      'Are you sure you want to delete this tag?',
      async () => {
        await executeApiAction({
          setLoading: setDeleting,
          dispatch,
          action: () => removePersonTag(selectedTag.ptsTagID),
          onSuccess: () => {
            dispatch(notify('Tag deleted successfully', 'success'));
            handleCloseDetails();
            onTagDeleted?.();
          },
        });
      },
      'Delete Tag'
    );
  }, [dispatch, selectedTag, requestConfirm, handleCloseDetails, onTagDeleted]);

  const handleAddTag = useCallback(() => onAddTag?.(), [onAddTag]);

  return (
    <Box className="rms-dialog-section person-tags-summary-card">
      <span className="person-tags-summary-card__tab">Tags</span>

      <Box className="person-tags-summary-card__icons">
        {activeTags.map((tag) => {
          const typeOption = tagTypeOptions?.find((option) => option.Code === tag.Type);
          const TagIcon = resolveIcon(typeOption?.Icon);
          const typeDescription = resolveDescription(tagTypeOptions, tag.Type);

          return (
            <RMSIconButton
              key={tag.ptsTagID}
              icon={<TagIcon className="person-tags-summary-card__tag-icon" />}
              tooltip={typeDescription}
              className="person-tags-summary-card__icon-btn"
              onClick={() => setSelectedTag(tag)}
            />
          );
        })}

        {activeTags.length === 0 && (
          <Typography variant="caption" color="textSecondary">
            No tags
          </Typography>
        )}
      </Box>

      <Box className="person-tags-summary-card__add">
        <RMSIconButton icon={<AddIcon />} tooltip="Add Tag" onClick={handleAddTag} />
      </Box>

      {selectedTag && (
        <RMSCommonDialog
          open={Boolean(selectedTag)}
          title="Tag Details"
          onClose={handleCloseDetails}
          maxWidth="xs"
          fullWidth
          saveButton={{ show: false }}
          cancelButton={{
            show: true,
            type: 'simple',
            label: 'Close',
            variant: 'outlined',
            color: 'secondary',
          }}
          customButtons={
            <RMSButton label="Delete" variant="outlined" color="secondary" onClick={handleDelete} />
          }>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="caption" color="textSecondary" component="div">
                Type
              </Typography>
              <Typography variant="body2">
                {resolveDescription(tagTypeOptions, selectedTag.Type)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="textSecondary" component="div">
                Description
              </Typography>
              <Typography variant="body2">{selectedTag.Value || '—'}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="textSecondary" component="div">
                Created
              </Typography>
              <Typography variant="body2">
                {selectedTag.Created ? formatDateTime(selectedTag.Created) : '—'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="textSecondary" component="div">
                Created By
              </Typography>
              <Typography variant="body2">{selectedTag.CreatedBy || '—'}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="textSecondary" component="div">
                Updated
              </Typography>
              <Typography variant="body2">
                {selectedTag.Updated ? formatDateTime(selectedTag.Updated) : '—'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="textSecondary" component="div">
                Updated By
              </Typography>
              <Typography variant="body2">{selectedTag.UpdatedBy || '—'}</Typography>
            </Grid>
          </Grid>
        </RMSCommonDialog>
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
          loading={deleting}
        />
      )}
    </Box>
  );
};

export default PersonTagsSummaryCard;
